import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { FC, KeyboardEvent } from 'react';
import styled, { css } from 'styled-components';

import {
  AIRewrite,
  Button,
  defaultThemeProp,
  Flex,
  getActiveElement,
  Icon,
  isInstance,
  Progress,
  useAfterInitialEffect,
  useElement,
  useI18n,
  useLiveLog,
  useOuterEvent
} from '@pega/cosmos-react-core';
import type { AIAction } from '@pega/cosmos-react-core';
import { extractMentions, restoreMentionPlaceholders } from '@pega/cosmos-react-rte';
import type { RichTextEditorState } from '@pega/cosmos-react-rte';

import FeedRichText from './FeedRichText';
import type { FeedEditRegionProps, RecipientMenuItem } from './Feed.types';
import { StyledActions } from './FeedInputRegion';
import FeedButton from './FeedButton';
import { FeedContext } from './Feed.context';
import FeedVisibilityControls from './FeedVisibilityControls';

const StyledInputContainer = styled.div(({ theme }) => {
  return css`
    width: calc(100% - (${theme.base.spacing} + ${theme.base['hit-area']['mouse-min']}));
    position: relative;

    @media (pointer: coarse) {
      width: calc(100% - (${theme.base.spacing} + ${theme.base['hit-area']['finger-min']}));
    }
  `;
});

StyledInputContainer.defaultProps = defaultThemeProp;

const FeedEditRegion: FC<FeedEditRegionProps> = (props: FeedEditRegionProps) => {
  const {
    defaultValue,
    variant,
    id,
    content,
    visibility,
    recipients = [],
    recipientInput,
    banners,
    progress,
    onRewriteClick,
    onEditSubmit,
    onEditCancel,
    emptyEditText,
    setEmptyEditText,
    onUnmount
  } = props;

  const t = useI18n();
  const { announcePolite } = useLiveLog();
  const { setShowSearchResults } = useContext(FeedContext);
  const rteRef = useRef<RichTextEditorState>(null);
  const editTextAreaRef = useRef<HTMLDivElement>(null);
  const postButtonRef = useRef<HTMLButtonElement>(null);
  const isSubmitButtonClicked = useRef(false);
  const previouslyFocusedElementRef = useRef<Element | null>(null);
  const [aiButtonEl, setAIButtonEl] = useElement<HTMLButtonElement>();
  const [showAIPopover, setShowAIPopover] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [suggestion, setSuggestion] = useState<string | undefined>(undefined);
  const [hasSuggestionContent, setHasSuggestionContent] = useState(false);
  const suggestionRteRef = useRef<RichTextEditorState>(null);

  // Sync AI-generated suggestion into the suggestion FeedRichText whenever it changes
  useEffect(() => {
    if (suggestion !== undefined) {
      suggestionRteRef.current?.clear();
      suggestionRteRef.current?.insertMarkdown?.(suggestion);
      setHasSuggestionContent(true);
    } else {
      suggestionRteRef.current?.clear();
      setHasSuggestionContent(false);
    }
  }, [suggestion]);

  const [contentModified, setContentModified] = useState(false);
  const [recipientsModified, setRecipientsModified] = useState(false);

  const valueRef = useRef(defaultValue ?? content ?? '');

  const announceLabel = t(variant === 'post' ? 'feed_edit_posted' : 'feed_edit_commented');

  useOuterEvent('mousedown', [editTextAreaRef], () => {
    if (emptyEditText) onEditCancel?.(id);
  });

  const handleEdit = () => {
    isSubmitButtonClicked.current = true;
    setShowSearchResults?.(false);
    if (!emptyEditText && (contentModified || recipientsModified)) {
      onEditSubmit({
        id,
        value: rteRef.current?.getPlainText() || '',
        visibility,
        recipients: recipientInput?.recipients ?? recipients
      });
      announcePolite({ message: announceLabel, type: 'success' });
    }
  };

  const handleGenerate = (rewriteAction: AIAction, additionalInstructions: string) => {
    const originalContent = rteRef.current?.getPlainText() || '';
    if (!originalContent) return;

    const { cleanText, mentionMap, userPlaceholders } = extractMentions(originalContent);

    // Capture in closure so concurrent Generate calls don't corrupt each other's restore map
    const capturedMentionMap = mentionMap;
    const capturedUserPlaceholders = userPlaceholders;

    setIsGenerating(true);
    setSuggestion(undefined);

    onRewriteClick?.(
      cleanText,
      rewriteAction,
      additionalInstructions,
      (rewrittenText: string) => {
        const restoredText = restoreMentionPlaceholders(
          rewrittenText,
          capturedMentionMap,
          capturedUserPlaceholders
        );
        setIsGenerating(false);
        setSuggestion(restoredText.trim() ? restoredText : undefined);
      },
      true
    );
  };

  const handleApplyRewrite = () => {
    const editedText = suggestionRteRef.current?.getPlainText() ?? suggestion ?? '';
    rteRef.current?.insertMarkdown?.(editedText);
    valueRef.current = editedText;

    // Manually update states since programmatic updates might not trigger onChange
    setEmptyEditText(editedText.trim() === '');
    setContentModified(editedText !== content);

    // Close popover and reset AI state
    setShowAIPopover(false);
    setSuggestion(undefined);

    // Return focus to RTE
    rteRef.current?.focus();
  };

  // If the API call fails, progress will flip back to false without calling the success callback
  // Clear the loading state so the Generate button re-enables
  useAfterInitialEffect(() => {
    if (!progress) setIsGenerating(false);
  }, [progress]);

  const onKeyDown = (event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') handleEdit();
  };

  useEffect(() => {
    if (rteRef.current) {
      rteRef.current?.focus();
    }
  }, [rteRef]);

  // Using layout effect to capture the focused element prior to editor taking focus.
  useLayoutEffect(() => {
    previouslyFocusedElementRef.current = getActiveElement();

    return () => {
      const el = previouslyFocusedElementRef.current;
      if (isInstance(el, HTMLElement) && el.isConnected) {
        el.focus();
      }
    };
  }, []);

  useEffect(() => {
    if (banners && isSubmitButtonClicked.current && postButtonRef.current) {
      postButtonRef.current.focus();
      isSubmitButtonClicked.current = false;
    }
  }, [banners]);

  useEffect(() => {
    if (recipientInput) {
      if (recipients.length !== recipientInput.recipients.length) {
        setRecipientsModified(true);
      } else {
        const recipientIntersection: RecipientMenuItem[] = [];
        recipients.forEach(recipient => {
          if (recipientInput.recipients.find(r => r.id === recipient.id)) {
            recipientIntersection.push(recipient);
          }
        });

        if (recipientIntersection.length !== recipientInput.recipients.length) {
          setRecipientsModified(true);
        } else {
          setRecipientsModified(false);
        }
      }
    }
  }, [recipients, recipientInput?.recipients]);

  const label = t(variant === 'post' ? 'feed_edit_post' : 'feed_edit_comment');

  useEffect(() => {
    return () => {
      onUnmount?.(valueRef.current);
    };
  }, []);

  return (
    <Flex container={{ gap: 1, alignItems: 'end' }}>
      <Flex
        as={StyledInputContainer}
        ref={editTextAreaRef}
        container={{ direction: 'column', gap: 1 }}
        inert={progress ? '' : undefined}
      >
        {banners}

        {visibility === 'private' && !!recipientInput && (
          <FeedVisibilityControls
            visibilityMode='private'
            visibility='private'
            recipientInput={recipientInput}
          />
        )}

        <FeedRichText
          label={label}
          onChange={() => {
            const value = rteRef.current?.getPlainText();
            setEmptyEditText(value?.trim() === '');
            // TODO: onChange is running on mount. It shouldn't...
            setContentModified(value !== content);
            valueRef.current = value ?? '';
          }}
          onKeyDown={onKeyDown}
          defaultValue={defaultValue}
          ref={rteRef}
        />

        <Progress
          visible={!!progress}
          focusOnVisible
          placement='local'
          message={typeof progress === 'string' ? progress : undefined}
        />
      </Flex>
      <Flex as={StyledActions} container={{ direction: 'column', gap: 0.25, pad: [0.25, 0] }}>
        <Button
          variant='simple'
          as={FeedButton}
          onClick={() => onEditCancel?.(id)}
          icon
          label={t('cancel')}
        >
          <Icon name='times' />
        </Button>

        {/* AI Rewrite Button */}
        {onRewriteClick && (
          <Button
            variant='simple'
            disabled={emptyEditText || !!progress}
            as={FeedButton}
            onClick={() => setShowAIPopover(true)}
            icon
            label={t('ai_rewrite_title')}
            ref={setAIButtonEl}
          >
            <Icon name='polaris' />
          </Button>
        )}

        <Button
          as={FeedButton}
          disabled={emptyEditText || (!contentModified && !recipientsModified) || !!progress}
          variant='simple'
          onClick={handleEdit}
          icon
          label={t('update')}
          ref={postButtonRef}
        >
          <Icon name='send' />
        </Button>
      </Flex>

      {/* AI Rewrite Popover */}
      {aiButtonEl && showAIPopover && onRewriteClick && (
        <AIRewrite
          target={aiButtonEl}
          isGenerating={isGenerating}
          suggestion={hasSuggestionContent ? suggestion : undefined}
          onGenerate={handleGenerate}
          onSubmit={handleApplyRewrite}
          suggestionEditor={
            <FeedRichText
              ref={suggestionRteRef}
              label={t('ai_rewrite_result_label')}
              labelHidden
              disabled={isGenerating || !suggestion?.trim()}
              readOnly={false}
              onChange={() =>
                setHasSuggestionContent(
                  (suggestionRteRef.current?.getPlainText() ?? '').trim().length > 0
                )
              }
            />
          }
          onCancel={() => {
            setShowAIPopover(false);
            setSuggestion(undefined);
            setIsGenerating(false);
            aiButtonEl.focus();
          }}
        />
      )}
    </Flex>
  );
};

export default FeedEditRegion;
